import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, Search, X, Pencil, Receipt, Trash2 } from "lucide-react";
import { api, formatError, money, fmtDateTime } from "../../lib/api";
import { AdminHeader, AdminModal, AField, ATextarea } from "../../components/AdminLayout";

const TABS = [
  { key: "all", label: "All" },
  { key: "paid", label: "Paid" },
  { key: "submitted", label: "Submitted" },
  { key: "pending", label: "Pending" },
  { key: "rejected", label: "Rejected" },
];

const STATUS_STYLE = {
  paid: "bg-[#4ADE80]/15 text-[#4ADE80]",
  submitted: "bg-[#E5A84B]/20 text-[#E5A84B]",
  pending: "bg-white/10 text-[#A19DB0]",
  rejected: "bg-[#E57373]/15 text-[#E57373]",
};

const StatusBadge = ({ status }) => (
  <span className={`inline-block text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 ${STATUS_STYLE[status] || "bg-white/10 text-[#A19DB0]"}`}>{status}</span>
);

export default function AdminPurchases() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [groups, setGroups] = useState([]);
  const [groupFilter, setGroupFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const load = () => { setLoading(true); api.get("/admin/purchases").then((r) => setOrders(r.data)).catch((e) => toast.error(formatError(e.response?.data?.detail))).finally(() => setLoading(false)); };
  useEffect(() => { load(); api.get("/pkg-groups").then((r) => setGroups(r.data || [])).catch(() => {}); }, []);

  const counts = useMemo(() => {
    const c = { all: orders.length };
    orders.forEach((o) => { c[o.status] = (c[o.status] || 0) + 1; });
    return c;
  }, [orders]);

  const filtered = useMemo(() => orders.filter((o) => {
    if (tab !== "all" && o.status !== tab) return false;
    if (groupFilter !== "all" && !(o.groups || []).includes(groupFilter)) return false;
    if (q) {
      const hay = `${o.reference} ${o.buyer_name || ""} ${o.buyer_email || ""} ${o.buyer_phone || ""} ${o.code || ""} ${(o.items || []).map((i) => i.name).join(" ")}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    if (from && (o.created_at || "") < from) return false;
    if (to && (o.created_at || "") > `${to}T23:59:59`) return false;
    return true;
  }), [orders, tab, groupFilter, q, from, to]);

  const revenue = useMemo(() => filtered.filter((o) => o.status === "paid").reduce((s, o) => s + (o.total || 0), 0), [filtered]);

  const codeTotals = useMemo(() => {
    const m = {};
    orders.filter((o) => o.code && o.status === "paid").forEach((o) => {
      if (!m[o.code]) m[o.code] = { code: o.code, count: 0, revenue: 0 };
      m[o.code].count += 1; m[o.code].revenue += (o.total || 0);
    });
    return Object.values(m).sort((a, b) => b.revenue - a.revenue);
  }, [orders]);

  const exportRows = (rows, suffix) => {
    if (rows.length === 0) { toast.message("Nothing to export with the current filters."); return; }
    const headers = ["Reference", "Date", "Buyer", "Email", "Phone", "Items", "Types", "Qty", "Subtotal", "Fee", "Tax", "Total", "Discount %", "Status", "Method", "Code", "Admin note"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [headers.join(",")];
    rows.forEach((o) => {
      const items = (o.items || []).map((i) => `${i.name}${i.option ? ` (${i.option})` : ""}`).join(" | ");
      lines.push([o.reference, fmtDateTime(o.created_at), o.buyer_name, o.buyer_email, o.buyer_phone, items, (o.groups || []).join(" | "), (o.items || []).length, o.subtotal, o.fee, o.tax, o.total, o.discount_percent, o.status, o.method, o.code, o.admin_note].map(esc).join(","));
    });
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `purchases_${suffix}_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} purchase${rows.length !== 1 ? "s" : ""}`);
  };
  const downloadCsv = () => exportRows(filtered, "all");
  const exportCode = (code) => exportRows(orders.filter((o) => o.code === code && o.status === "paid"), code.toLowerCase());

  const saveEdit = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/admin/purchases/${edit.id}`, { method: edit.method || "", admin_note: edit.admin_note || "" });
      setOrders((os) => os.map((o) => (o.id === r.data.id ? { ...o, ...r.data } : o)));
      toast.success("Purchase updated");
      setEdit(null);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const allSelected = filtered.length > 0 && filtered.every((o) => selected.has(o.id));
  const toggleAll = () => setSelected(() => (allSelected ? new Set() : new Set(filtered.map((o) => o.id))));
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const doDelete = async (ids) => {
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} purchase record${ids.length !== 1 ? "s" : ""}? This removes ${ids.length !== 1 ? "them" : "it"} from history and from the Reports revenue. This cannot be undone.`)) return;
    try {
      const r = await api.post("/admin/purchases/bulk-delete", { ids });
      toast.success(`Deleted ${r.data.deleted} purchase${r.data.deleted !== 1 ? "s" : ""}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-purchases">
      <AdminHeader title="Purchases" subtitle="Every ticket purchase — filter, inspect and export."
        action={<div className="flex items-center gap-2">
          {selected.size > 0 && <button onClick={() => doDelete([...selected])} data-testid="purchases-delete-selected" className="flex items-center gap-2 rounded-full bg-[#E57373] text-white font-semibold px-4 py-2 text-sm active:scale-95 transition-transform"><Trash2 size={16} /> Delete ({selected.size})</button>}
          <button onClick={downloadCsv} data-testid="purchases-download-csv" className="flex items-center gap-2 rounded-full bg-[#D4AF37] text-black font-semibold px-4 py-2 text-sm active:scale-95 transition-transform"><Download size={16} /> Download CSV</button>
        </div>} />

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
        <div className="rounded-2xl bg-white/5 border border-white/10 p-4"><p className="text-[#A19DB0] text-xs">Showing</p><p className="font-display text-2xl font-bold" data-testid="purchases-count">{filtered.length}</p></div>
        <div className="rounded-2xl bg-white/5 border border-white/10 p-4"><p className="text-[#A19DB0] text-xs">Paid revenue (filtered)</p><p className="font-display text-2xl font-bold text-[#D4AF37]">{money(revenue)}</p></div>
        <div className="rounded-2xl bg-white/5 border border-white/10 p-4 col-span-2 sm:col-span-1"><p className="text-[#A19DB0] text-xs">Total purchases</p><p className="font-display text-2xl font-bold">{orders.length}</p></div>
      </div>

      <div className="flex flex-nowrap gap-2 mb-4 overflow-x-auto no-scrollbar" data-testid="purchases-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`purchases-tab-${t.key}`} className={`shrink-0 whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm transition-colors ${tab === t.key ? "bg-[#D4AF37] text-black border-[#D4AF37] font-semibold" : "border-white/10 bg-white/5 text-[#E8E4F0] hover:border-[#D4AF37]/50"}`}>
            {t.label}{counts[t.key] != null ? ` (${counts[t.key]})` : ""}
          </button>
        ))}
      </div>

      {groups.length > 0 && (
        <div className="flex flex-nowrap items-center gap-2 mb-4 overflow-x-auto no-scrollbar" data-testid="purchases-group-tabs">
          <span className="text-[#A19DB0] text-xs uppercase tracking-wide shrink-0 mr-1">Type</span>
          {[{ key: "all", label: "All" }, ...groups.map((g) => ({ key: g.key, label: g.label }))].map((g) => (
            <button key={g.key} onClick={() => setGroupFilter(g.key)} data-testid={`purchases-group-${g.key}`} className={`shrink-0 whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm transition-colors ${groupFilter === g.key ? "bg-[#9D4EDD] text-white border-[#9D4EDD] font-semibold" : "border-white/10 bg-white/5 text-[#E8E4F0] hover:border-[#9D4EDD]/60"}`}>{g.label}</button>
          ))}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2 flex-1">
          <Search size={16} className="text-[#A19DB0] shrink-0" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="purchases-search" placeholder="Search reference, name, email, item, code…" className="flex-1 min-w-0 bg-transparent outline-none text-sm placeholder:text-white/30" />
          {q && <button onClick={() => setQ("")} className="text-[#A19DB0]"><X size={15} /></button>}
        </div>
        <div className="flex items-center gap-2">
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="purchases-from" className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#D4AF37]" />
          <span className="text-[#A19DB0] text-sm">to</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="purchases-to" className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#D4AF37]" />
        </div>
      </div>

      {codeTotals.length > 0 && (
        <div className="mb-5" data-testid="code-totals">
          <p className="text-[#A19DB0] text-xs uppercase tracking-wide mb-2">Promo / special codes · paid revenue</p>
          <div className="flex flex-wrap gap-2">
            {codeTotals.map((c) => (
              <div key={c.code} data-testid={`code-total-${c.code}`} className={`flex items-center gap-2 rounded-full border pl-3.5 pr-1.5 py-1 text-sm transition-colors ${q === c.code ? "border-[#5CC8FF] bg-[#5CC8FF]/20" : "border-[#5CC8FF]/40 bg-[#5CC8FF]/10"}`}>
                <button onClick={() => setQ(q === c.code ? "" : c.code)} data-testid={`code-filter-${c.code}`} className="flex items-center gap-2">
                  <span className="font-bold text-[#5CC8FF]">{c.code}</span>
                  <span className="text-[#A19DB0]">{c.count} sale{c.count !== 1 ? "s" : ""} · {money(c.revenue)}</span>
                </button>
                <button onClick={() => exportCode(c.code)} data-testid={`code-export-${c.code}`} title={`Export ${c.code} sales`} className="p-1.5 rounded-full hover:bg-white/10 text-[#5CC8FF]"><Download size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-[#A19DB0] text-sm">Loading purchases…</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl bg-white/5 border border-white/10 p-8 text-center text-[#A19DB0] text-sm">No purchases match your filters.</div>
      ) : (
        <div className="rounded-2xl border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]">
              <thead>
                <tr className="text-left text-[#A19DB0] text-xs uppercase tracking-wide bg-white/5">
                  <th className="px-4 py-3 w-10"><input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="purchases-select-all" className="accent-[#D4AF37]" /></th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Reference</th>
                  <th className="px-4 py-3">Buyer</th>
                  <th className="px-4 py-3">Items</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Method</th>
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => (
                  <tr key={o.id} data-testid={`purchase-row-${o.id}`} className={`border-t border-white/10 hover:bg-white/[0.03] cursor-pointer ${selected.has(o.id) ? "bg-[#D4AF37]/[0.06]" : ""}`} onClick={() => setDetail(o)}>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selected.has(o.id)} onChange={() => toggleOne(o.id)} data-testid={`purchase-select-${o.id}`} className="accent-[#D4AF37]" /></td>
                    <td className="px-4 py-3 whitespace-nowrap text-[#A19DB0]">{fmtDateTime(o.created_at)}</td>
                    <td className="px-4 py-3 font-mono text-[#D4AF37]">{o.reference}</td>
                    <td className="px-4 py-3"><div className="min-w-0"><p className="truncate">{o.buyer_name || "—"}</p><p className="text-[#A19DB0] text-xs truncate">{o.buyer_email}</p></div></td>
                    <td className="px-4 py-3 text-[#E8E4F0]"><span className="line-clamp-1">{(o.items || []).map((i) => i.name).join(", ") || "—"}</span></td>
                    <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">{money(o.total)}</td>
                    <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
                    <td className="px-4 py-3 text-[#A19DB0] whitespace-nowrap">{o.method || "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{o.code ? <span data-testid={`purchase-code-${o.id}`} className="text-[10px] uppercase font-bold rounded-full px-2 py-0.5 bg-[#5CC8FF]/15 text-[#5CC8FF]">{o.code}</span> : <span className="text-[#A19DB0]">—</span>}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={(e) => { e.stopPropagation(); setEdit({ ...o }); }} data-testid={`purchase-edit-${o.id}`} className="text-[#A19DB0] hover:text-white mr-3"><Pencil size={15} /></button>
                      <button onClick={(e) => { e.stopPropagation(); doDelete([o.id]); }} data-testid={`purchase-delete-${o.id}`} className="text-[#A19DB0] hover:text-[#E57373]"><Trash2 size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setDetail(null)} data-testid="purchase-detail-modal">
          <div className="w-full max-w-md rounded-3xl bg-[#140F1E] border border-white/10 p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <p className="font-display text-xl font-bold flex items-center gap-2"><Receipt size={18} className="text-[#D4AF37]" /> {detail.reference}</p>
                <p className="text-[#A19DB0] text-xs mt-1">{fmtDateTime(detail.created_at)}</p>
              </div>
              <StatusBadge status={detail.status} />
            </div>
            <div className="space-y-1.5 text-sm">
              <Row label="Buyer" value={detail.buyer_name} />
              <Row label="Email" value={detail.buyer_email} />
              <Row label="Phone" value={detail.buyer_phone} />
              <Row label="Method" value={detail.method || "—"} />
              {detail.code && <Row label="Code" value={detail.code} />}
              {detail.admin_note && <Row label="Admin note" value={detail.admin_note} />}
            </div>
            <div className="mt-4 rounded-2xl bg-white/5 border border-white/10 p-3 space-y-2">
              {(detail.items || []).map((it, i) => (
                <div key={i} className="flex items-start justify-between gap-3 text-sm" data-testid={`purchase-item-${i}`}>
                  <div className="min-w-0">
                    <p className="truncate">{it.name}{it.option ? ` · ${it.option}` : ""}{it.quarter_label ? ` · ${it.quarter_label}` : ""}</p>
                    {it.social_title && <p className="text-[#A19DB0] text-xs">{it.social_title}{it.social_date ? ` · ${fmtDateTime(it.social_date)}` : ""}</p>}
                    {(it.group_member_ids || []).length > 0 && <p className="text-[#A19DB0] text-xs">Group of {(it.group_member_ids || []).length + 1}</p>}
                  </div>
                  <span className="text-[#A19DB0] whitespace-nowrap">{money(it.price)}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 space-y-1.5 text-sm border-t border-white/10 pt-3">
              <Row label="Subtotal" value={money(detail.subtotal)} />
              {detail.discount_percent > 0 && <Row label="Discount" value={`−${detail.discount_percent}%`} />}
              {detail.fee > 0 && <Row label="Fee" value={money(detail.fee)} />}
              {detail.tax > 0 && <Row label="Tax" value={money(detail.tax)} />}
              <div className="flex items-center justify-between pt-1"><span className="text-[#A19DB0]">Total</span><span className="font-display text-xl font-bold text-[#D4AF37]">{money(detail.total)}</span></div>
            </div>
            {detail.proof_url && <a href={detail.proof_url} target="_blank" rel="noreferrer" className="mt-4 inline-block text-[#D4AF37] text-sm underline">View payment proof</a>}
            <div className="mt-5 flex gap-2">
              <button onClick={() => { setEdit({ ...detail }); setDetail(null); }} className="flex-1 rounded-full bg-white/10 border border-white/10 py-2.5 text-sm font-medium">Edit</button>
              <button onClick={() => setDetail(null)} data-testid="purchase-detail-close" className="flex-1 rounded-full bg-[#D4AF37] text-black py-2.5 text-sm font-semibold">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      <AdminModal open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title={`Edit ${edit?.reference || ""}`} onSubmit={saveEdit} busy={busy}>
        {edit && (
          <>
            <p className="text-xs text-[#A19DB0]">Editing is limited to payment method and an internal note. To approve/reject and grant passes, use the Payments page.</p>
            <AField label="Payment method" value={edit.method || ""} onChange={(e) => setEdit((s) => ({ ...s, method: e.target.value }))} data-testid="purchase-edit-method" placeholder="e.g. PayNow" />
            <ATextarea label="Admin note" value={edit.admin_note || ""} onChange={(e) => setEdit((s) => ({ ...s, admin_note: e.target.value }))} data-testid="purchase-edit-note" placeholder="Internal note for this purchase" />
          </>
        )}
      </AdminModal>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3"><span className="text-[#A19DB0]">{label}</span><span className="text-right truncate">{value || "—"}</span></div>
);
